import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  Dialog,
  Heading,
  IconInvoice,
  IconPeople,
  IconTick,
  List,
  Notice,
  Stack,
  Text,
  useResponsiveValue,
} from 'braid-design-system';
import type { FC, PropsWithChildren } from 'react';

import { Modal } from 'src/libs/Modal';

import translations from './.vocab';

export type MandatoryQuestions =
  | {
      success: true;
      data: Array<{ question: string; answers: string[] }>;
    }
  | {
      success: false;
    };

export type RelevantApplicationStats =
  | {
      success: true;
      billableApplicantsCount: number;
      maxBillableApplicants: number;
    }
  | {
      success: false;
    };

interface BillableApplicantsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mandatoryQuestions: MandatoryQuestions;
  relevantApplicationStats: RelevantApplicationStats;
}

export const RelevantApplicationDialog: FC<
  PropsWithChildren<BillableApplicantsDialogProps>
> = ({ isOpen, onClose, mandatoryQuestions, relevantApplicationStats }) => {
  const { t } = useTranslations(translations);
  const responsiveValue = useResponsiveValue();

  const isMobile = responsiveValue({
    mobile: true,
    tablet: false,
    desktop: false,
    wide: false,
  });

  // FIXME: Make this responsive
  const modalWidth = {
    wide: '653px',
    tablet: '55%',
    desktop: '55%',
    mobile: null,
  };

  if (!relevantApplicationStats.success) {
    return isMobile ? (
      <Dialog
        title=""
        open={isOpen}
        onClose={onClose}
        id="relevantApplicationModal"
      >
        <Notice tone="critical">
          <Text>
            {t(
              'We can’t display the relevant applications count right now. Try refreshing the page or check back later. Individual relevant applications are marked with a badge.',
            )}
          </Text>
        </Notice>
        <Stack space="medium">
          <Stack space="small">
            <Text size="small" weight="strong">
              {t('How does SEEK define relevant application?')}
            </Text>
            <Text size="small" tone="secondary">
              {t(
                'Applications are relevant if they provide these answers to screening questions:',
              )}
            </Text>
          </Stack>
          <Stack space="small">
            {!mandatoryQuestions.success ? (
              <Notice tone="critical">
                <Text size="small">
                  {t(
                    'We can’t load the questions and answers right now. Refresh the page or check back later.',
                  )}
                </Text>
              </Notice>
            ) : (
              <Stack space="medium">
                {mandatoryQuestions.data.map(({ answers, question }, index) => (
                  <Stack space="small" key={index}>
                    <Text size="small" weight="strong">
                      {question}
                    </Text>
                    <List size="small" tone="secondary" space="small">
                      {answers.map((answer) => (
                        <Text size="small" tone="secondary" key={answer}>
                          {answer}
                        </Text>
                      ))}
                    </List>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
          <Stack space="small">
            <Text size="small" weight="strong">
              {t('You can still filter by your own criteria')}
            </Text>
            <Text size="small" tone="secondary">
              {t(
                "You can still view and filter all applications according to your preferred criteria, but this won't affect the payment terms of relevant applications.",
              )}
            </Text>
          </Stack>
          <Text size="xsmall">
            <span style={{ fontStyle: 'italic' }}>
              {t(
                'SEEK does not verify claims made in candidate applications. SEEK Pass verification is not required.',
              )}
            </span>
          </Text>
        </Stack>
      </Dialog>
    ) : (
      <Modal
        width={modalWidth}
        isOpen={isOpen}
        close={onClose}
        titleId="billableApplicantModal"
      >
        <Box padding="large" height="full" style={{ width: '95%' }}>
          <Stack space="large">
            <Notice tone="critical">
              <Text>
                {t(
                  'We can’t display the relevant applications count right now. Try refreshing the page or check back later. Individual relevant applications are marked with a badge.',
                )}
              </Text>
            </Notice>
            <Stack space="medium">
              <Stack space="small">
                <Text size="small" weight="strong">
                  {t('How does SEEK define relevant application?')}
                </Text>
                <Text size="small" tone="secondary">
                  {t(
                    'Applications are relevant if they provide these answers to screening questions:',
                  )}
                </Text>
              </Stack>
              {!mandatoryQuestions.success ? (
                <Notice tone="critical">
                  <Text>
                    {t(
                      'We can’t load the questions and answers right now. Refresh the page or check back later.',
                    )}
                  </Text>
                </Notice>
              ) : (
                <Stack space="medium">
                  {mandatoryQuestions.data.map(
                    ({ answers, question }, index) => (
                      <Stack space="small" key={index}>
                        <Text size="small" weight="strong">
                          {question}
                        </Text>
                        <List size="small" tone="secondary" space="small">
                          {answers.map((answer) => (
                            <Text size="small" tone="secondary" key={answer}>
                              {answer}
                            </Text>
                          ))}
                        </List>
                      </Stack>
                    ),
                  )}
                </Stack>
              )}
              <Stack space="small">
                <Text size="small" weight="strong">
                  {t('You can still filter by your own criteria')}
                </Text>
                <Text size="small" tone="secondary">
                  {t(
                    "You can still view and filter all applications according to your preferred criteria, but this won't affect the payment terms of relevant applications.",
                  )}
                </Text>
              </Stack>
              <Text size="xsmall">
                <span style={{ fontStyle: 'italic' }}>
                  {t(
                    'SEEK does not verify claims made in candidate applications. SEEK Pass verification is not required.',
                  )}
                </span>
              </Text>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    );
  }

  return isMobile ? (
    <Dialog
      open={isOpen}
      onClose={onClose}
      id="relevantApplicationModal"
      title={t(
        'You have {billableApplicantsCount}/{maxBillableApplicants} relevant applications',
        {
          maxBillableApplicants: relevantApplicationStats.maxBillableApplicants,
          billableApplicantsCount:
            relevantApplicationStats.billableApplicantsCount,
        },
      )}
    >
      <Box
        background="promoteLight"
        paddingY="medium"
        paddingX="medium"
        borderRadius="large"
        position="relative"
        overflow="hidden"
        role="alert"
        aria-live="polite"
      >
        <Stack space="small">
          <Columns space="small">
            <Column width="content">
              <IconTick size="xsmall" tone="promote" />
            </Column>
            <Column>
              <Box paddingY="xxsmall">
                <Text size="small">
                  {t(
                    'We committed to delivering at least {maxBillableApplicants} relevant applications.',
                    {
                      maxBillableApplicants:
                        relevantApplicationStats.maxBillableApplicants,
                    },
                  )}
                </Text>
              </Box>
            </Column>
          </Columns>
          <Columns space="small">
            <Column width="content">
              <IconPeople size="xsmall" tone="promote" />
            </Column>
            <Column>
              <Box paddingY="xxsmall">
                <Text size="small">
                  {t(
                    'Pay less for each relevant application short of {maxBillableApplicants}.',
                    {
                      maxBillableApplicants:
                        relevantApplicationStats.maxBillableApplicants,
                    },
                  )}
                </Text>
              </Box>
            </Column>
          </Columns>
        </Stack>
      </Box>
      <Stack space="medium">
        <Stack space="small">
          <Text size="small" weight="strong">
            {t('How does SEEK define relevant application?')}
          </Text>
          <Text size="small" tone="secondary">
            {t(
              'Applications are relevant if they provide these answers to screening questions:',
            )}
          </Text>
        </Stack>
        <Stack space="small">
          {!mandatoryQuestions.success ? (
            <Notice tone="critical">
              <Text size="small">
                {t(
                  'We can’t load the questions and answers right now. Refresh the page or check back later.',
                )}
              </Text>
            </Notice>
          ) : (
            <Stack space="medium">
              {mandatoryQuestions.data.map(({ answers, question }, index) => (
                <Stack space="small" key={index}>
                  <Text size="small" weight="strong">
                    {question}
                  </Text>
                  <List size="small" tone="secondary" space="small">
                    {answers.map((answer) => (
                      <Text size="small" tone="secondary" key={answer}>
                        {answer}
                      </Text>
                    ))}
                  </List>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
        <Stack space="small">
          <Text size="small" weight="strong">
            {t('You can still filter by your own criteria')}
          </Text>
          <Text size="small" tone="secondary">
            {t(
              "You can still view and filter all applications according to your preferred criteria, but this won't affect the payment terms of relevant applications.",
            )}
          </Text>
        </Stack>
        <Text size="xsmall">
          <span style={{ fontStyle: 'italic' }}>
            {t(
              'SEEK does not verify claims made in candidate applications. SEEK Pass verification is not required.',
            )}
          </span>
        </Text>
      </Stack>
    </Dialog>
  ) : (
    <Modal
      width={modalWidth}
      isOpen={isOpen}
      close={onClose}
      titleId="relevantApplicationModal"
    >
      <Box display="flex">
        <Columns space="none">
          <Column width="3/5">
            <Box padding="large" height="full">
              <Stack space="medium">
                <Heading level="3">
                  {t(
                    'You have {billableApplicantsCount}/{maxBillableApplicants} relevant applications',
                    {
                      maxBillableApplicants:
                        relevantApplicationStats.maxBillableApplicants,
                      billableApplicantsCount:
                        relevantApplicationStats.billableApplicantsCount,
                    },
                  )}
                </Heading>
                <Stack space="small">
                  <Text size="small" weight="strong">
                    {t('How does SEEK define relevant application?')}
                  </Text>
                  <Text size="small" tone="secondary">
                    {t(
                      'Applications are relevant if they provide these answers to screening questions:',
                    )}
                  </Text>
                </Stack>
                <Stack space="small">
                  {!mandatoryQuestions.success ? (
                    <Notice tone="critical">
                      <Text>
                        {t(
                          'We can’t load the questions and answers right now. Refresh the page or check back later.',
                        )}
                      </Text>
                    </Notice>
                  ) : (
                    <Stack space="medium">
                      {mandatoryQuestions.data.map(
                        ({ answers, question }, index) => (
                          <Stack space="small" key={index}>
                            <Text size="small" weight="strong">
                              {question}
                            </Text>
                            <List size="small" tone="secondary" space="small">
                              {answers.map((answer) => (
                                <Text
                                  size="small"
                                  tone="secondary"
                                  key={answer}
                                >
                                  {answer}
                                </Text>
                              ))}
                            </List>
                          </Stack>
                        ),
                      )}
                    </Stack>
                  )}
                </Stack>
                <Stack space="small">
                  <Text size="small" weight="strong">
                    {t('You can still filter by your own criteria')}
                  </Text>
                  <Text size="small" tone="secondary">
                    {t(
                      "You can still view and filter all applications according to your preferred criteria, but this won't affect the payment terms of relevant applications.",
                    )}
                  </Text>
                </Stack>
                <Text size="xsmall">
                  <span style={{ fontStyle: 'italic' }}>
                    {t(
                      'SEEK does not verify claims made in candidate applications. SEEK Pass verification is not required.',
                    )}
                  </span>
                </Text>
              </Stack>
            </Box>
          </Column>
          <Column width="2/5">
            <Box
              display="flex"
              paddingY="medium"
              paddingLeft="medium"
              paddingRight="large"
              alignItems="center"
              background="promoteLight"
              height="full"
              style={{
                borderRadius: '0 6px 6px 0',
              }}
            >
              <Stack space="large">
                <Columns space="xsmall">
                  <Column width="content">
                    <IconTick size="xsmall" tone="promote" />
                  </Column>
                  <Column>
                    <Box paddingY="xxsmall">
                      <Stack space="small">
                        <Text size="small" weight="strong">
                          {t('Receive relevant applications')}
                        </Text>
                        <Text size="small">
                          {t(
                            'We committed to delivering at least {maxBillableApplicants} relevant applications.',
                            {
                              maxBillableApplicants:
                                relevantApplicationStats.maxBillableApplicants,
                            },
                          )}
                        </Text>
                      </Stack>
                    </Box>
                  </Column>
                </Columns>
                <Columns space="xsmall">
                  <Column width="content">
                    <IconPeople size="xsmall" tone="promote" />
                  </Column>
                  <Column>
                    <Box paddingY="xxsmall">
                      <Stack space="small">
                        <Text size="small" weight="strong">
                          {t('Pay less if we don’t deliver')}
                        </Text>
                        <Text size="small">
                          {t(
                            'Pay less for each relevant application short of {maxBillableApplicants}. Relevant applications are marked with a badge.',
                            {
                              maxBillableApplicants:
                                relevantApplicationStats.maxBillableApplicants,
                            },
                          )}
                        </Text>
                      </Stack>
                    </Box>
                  </Column>
                </Columns>
                <Columns space="xsmall">
                  <Column width="content">
                    <IconInvoice size="xsmall" tone="promote" />
                  </Column>
                  <Column>
                    <Box paddingY="xxsmall">
                      <Stack space="small">
                        <Text size="small" weight="strong">
                          {t('Pay at the end')}
                        </Text>
                        <Text size="small">
                          {t('We’ll send your invoice at the end of your ad.')}
                        </Text>
                      </Stack>
                    </Box>
                  </Column>
                </Columns>
              </Stack>
            </Box>
          </Column>
        </Columns>
      </Box>
    </Modal>
  );
};
